import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { AssayUploadDialog } from "@/components/symin/AssayUploadDialog";
import { Button } from "@/components/ui/button";
import { Flask, ArrowSquareOut, Info } from "@phosphor-icons/react";

function FitAll({ fc }) {
  const map = useMap();
  useEffect(() => {
    try { const b = L.geoJSON(fc).getBounds(); if (b.isValid()) map.fitBounds(b, { padding: [30, 30] }); } catch (e) { /* */ }
  }, [fc, map]);
  useEffect(() => { const t = setTimeout(() => map.invalidateSize({ animate: false }), 80); return () => clearTimeout(t); }, [map]);
  return null;
}

function gradeColor(v, max) {
  if (v == null || !max) return "#64748B";
  const t = Math.min(1, v / max);
  return t > 0.66 ? "#DC2626" : t > 0.33 ? "#F59E0B" : "#FCD34D";
}

export default function Geochemistry() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/projects/${id}/geochemistry`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  const boundaryFC = useMemo(() => ({
    type: "FeatureCollection",
    features: (data?.areas || []).filter((a) => a.boundary).map((a) => ({ type: "Feature", geometry: a.boundary, properties: {} })),
  }), [data]);
  const samples = data?.samples || [];
  const maxG = data?.stats?.max || 0;
  const noSamples = !loading && samples.length === 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="geochem" />
      <div className="ml-[64px] flex flex-col h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <Flask size={18} style={{ color: "#E67332" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="geochem-title">Geochemistry</h1>
          <span className="text-xs text-muted-foreground truncate hidden lg:block">{data?.project?.name}</span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <AssayUploadDialog pid={id} areas={data?.areas || []} onUploaded={load} />
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="geochem-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
          <aside className="w-full lg:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-border bg-card overflow-y-auto max-h-[40vh] lg:max-h-none" data-testid="geochem-panel">
            <div className="px-4 py-3 border-b border-border grid grid-cols-3 gap-2 text-center">
              <div><div className="font-mono font-bold text-lg text-foreground" data-testid="geochem-sample-count">{data?.stats?.count ?? 0}</div><div className="text-[9px] uppercase tracking-wide text-muted-foreground">Samples</div></div>
              <div><div className="font-mono font-bold text-lg" style={{ color: "#E67332" }}>{data?.stats?.max ?? "-"}</div><div className="text-[9px] uppercase tracking-wide text-muted-foreground">Max{data?.grade_field ? ` ${data.grade_field}` : ""}</div></div>
              <div><div className="font-mono font-bold text-lg text-foreground">{data?.targets?.length ?? 0}</div><div className="text-[9px] uppercase tracking-wide text-muted-foreground">Targets</div></div>
            </div>
            {noSamples ? (
              <div className="px-4 py-6 text-center" data-testid="geochem-empty">
                <Info size={24} className="mx-auto mb-2" style={{ color: "#E67332" }} />
                <p className="text-xs text-muted-foreground leading-relaxed">No assay or soil-geochemistry samples have been uploaded yet. Add drillhole / assay data in <span className="text-foreground">Data &amp; Setup</span> to map sample anomalies against the exploration target corridors shown on the map.</p>
              </div>
            ) : (
              <div className="px-3 py-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">Top Anomalies</div>
                <div className="space-y-1">
                  {(data?.anomalies || []).map((s, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent/40" data-testid={`geochem-anomaly-${i}`}>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: gradeColor(s.grade, maxG) }} />
                      <span className="font-mono text-xs text-foreground">{s.id}</span>
                      <span className="font-mono text-xs ml-auto" style={{ color: "#E67332" }}>{s.grade}</span>
                      <span className="text-[10px] text-muted-foreground w-20 truncate text-right">{s.nearest_target ? `${s.nearest_km}km → ${s.nearest_target}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <div className="relative flex-1 min-w-0 bg-[#04070B]">
            {loading ? (
              <div className="absolute inset-0 grid place-items-center text-muted-foreground text-sm">Loading geochemistry…</div>
            ) : (
              <div className="absolute inset-0" data-testid="geochem-map">
                <MapContainer style={{ height: "100%", width: "100%", background: "#04070B" }} center={[0, 20]} zoom={5} attributionControl={true}>
                  <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="© Esri" maxZoom={19} />
                  {boundaryFC.features.length > 0 && <GeoJSON data={boundaryFC} style={{ color: "#C20511", weight: 1.5, fill: false }} />}
                  {(data?.targets || []).map((t, i) => (
                    <CircleMarker key={`t${i}`} center={[t.lat, t.lon]} radius={4 + (t.prospectivity / 100) * 4}
                      pathOptions={{ color: "#04070B", weight: 1, fillColor: "#C20511", fillOpacity: 0.5 }}>
                      <Popup><span className="font-mono text-xs">{t.label} · {t.priority}</span></Popup>
                    </CircleMarker>
                  ))}
                  {samples.map((s, i) => (
                    <CircleMarker key={`s${i}`} center={[s.lat, s.lon]} radius={6}
                      pathOptions={{ color: "#04070B", weight: 1.5, fillColor: gradeColor(s.grade, maxG), fillOpacity: 0.95 }}>
                      <Popup><div className="font-mono text-xs">{s.id}<br />Grade: {s.grade}<br />{s.nearest_target ? `${s.nearest_km} km to ${s.nearest_target}` : ""}</div></Popup>
                    </CircleMarker>
                  ))}
                  {boundaryFC.features.length > 0 && <FitAll fc={boundaryFC} />}
                </MapContainer>
                {noSamples && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] px-4 py-2 rounded-md bg-card/95 backdrop-blur-md border border-border text-xs text-muted-foreground" data-testid="geochem-map-note">
                    Showing {data?.targets?.length || 0} exploration targets - upload assays to overlay geochemistry anomalies.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
